import React, { useContext } from 'react';
import {
  Modal,
  ModalConfirm,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import useReactHookFormProps from '../../hooks/useReactHookFormProps';
import { AppContext } from '../App/context';
import { usePropertyBatchModal } from './hooks';
import { dropdownValues } from './dropdownValues';
import messages from './messages';

const CreatePropertyDataBatch = (props): Props => {
  const { selectedProperty } = useContext(AppContext);

  const handleCancelConfirm = () => {
    const { history } = props;
    history.goBack();
  };
  const ReactHookFormProps = useReactHookFormProps({});
  const programMatches = {
    rd: /\bRD\b/i,
    hud: /\bHUD\b/i,
    home: /\bHOME\b/i,
    noah: /\bNOAH\b/i,
    deferredLoan: /Deferred\s+Loan/i,
    lihtc: /\bLIHTC\b/i,
    vhcb: /\bVHCB\b/i,
    nsp: /\bNSP\b/i,
    ahp: /\bAHP\b/i,
    kcmo: /\bKCMO\b/i,
    stLouisFunds: /\bSt\.?\s*Louis\s*Funds\b/i,
    htf: /\bHTF\b/i,
    tcap: /\bTCAP\b/i,
    ph: /\bPH\b/i,
  };

  // Create a map of which programs are present
  const availablePrograms = Object.entries(programMatches).reduce(
    (acc, [key, regex]) => {
      acc[key] = selectedProperty.pap.some((fpap) =>
        regex.test(fpap?.masterAffordableProgram?.name),
      );
      return acc;
    },
    {},
  );
  // check if the waitlist feature is on for the property:
  const isWaitlistFeatureOn = selectedProperty?.setup?.affordableWaitlist;

  // Filter dropdown values based on available programs
  const filteredDropdownValues = dropdownValues.filter((value) =>
    Object.entries(value)
      .filter(([key]) => key in programMatches) // Only check program-related properties
      .every(([program, required]) => !required || availablePrograms[program]),
  );
  // filter out the waitlist option if the feature is not on
  const filteredDropdownValuesWithoutWaitlist = filteredDropdownValues.filter(
    (value) => {
      if (isWaitlistFeatureOn) {
        return true; // Keep the option if the feature is on
      }
      return value.value !== 'waitlist'; // Remove the option if the feature is off
    },
  );

  const { modalProps, confirmModalProps, formatMessage } =
    usePropertyBatchModal({
      propertyName: selectedProperty.name,
      propertyId: selectedProperty.id,
      organizationId: selectedProperty.organizationId,
      handleCancelConfirm,
      formProps: ReactHookFormProps,
    });

  const formProps = {
    fields: [
      {
        variant: 'fileUploader',
        name: 'file',
        // .asc files have no MIME type in browsers — allow '' when a property has PH program
        requiredFileType: [
          'text/xml',
          'text/csv',
          'text/plain',
          ...(availablePrograms.ph ? [''] : []),
        ],
        sx: { margin: 'auto' },
        rules: { required: true },
        GridProps: {
          xs: 12,
        },
      },
      {
        name: 'note',
        variant: 'custom',
        component: (
          <Typography variant={'bodySmall'}>
            {formatMessage(messages.uploadFileNote)}
          </Typography>
        ),
        GridProps: { xs: 12 },
      },
      {
        name: 'propertyName',
        label: formatMessage(messages.propertyName),
        variant: 'text',
        disabled: true,
        value: selectedProperty.name,
        GridProps: { xs: 12 },
      },
      {
        variant: 'singleSelect',
        name: 'fileType',
        label: formatMessage(messages.fileType),
        options: filteredDropdownValuesWithoutWaitlist,
        rules: { required: true },
        GridProps: { xs: 12 },
      },
      {
        name: 'helperText',
        variant: 'custom',
        component: (
          <Typography variant={'bodySmall'}>
            {formatMessage(messages.uploadText)}
            {formatMessage(messages.emailText)}
          </Typography>
        ),
        GridProps: { xs: 12 },
      },
    ],
  };

  return (
    <>
      <Modal onClose={handleCancelConfirm} {...modalProps}>
        <Form ReactHookFormProps={ReactHookFormProps} {...formProps} />
      </Modal>
      <ModalConfirm onClose={handleCancelConfirm} {...confirmModalProps} />
    </>
  );
};

export default CreatePropertyDataBatch;
